// AppointmentForm component for NAMHS appointment website
// Version v1.1.2.0 Update made on 01/19/2025
// Updated to use new API endpoints and multi-step form workflow:
//
// MULTI-STEP FORM WORKFLOW:
// Step 1: New or Existing User Selection
// Step 2a: New Patient Data Entry (with debug logging)
// Step 2b: Existing Patient Search (with address parsing and zipcode lookup)
// Step 3: Provider Selection (Medical and Psychiatric providers)
// Step 4: Review Data and Submit (with debug parameters for booking/book endpoint)
//
// NEW API ENDPOINTS (from SCHEMA_RESTRUCTURE_AND_API_REFERENCE.md):
// - GET /patient/start - Start new patient flow
// - POST /patient/zipcode - ZIP code lookup
// - POST /patient/save-new-patient - Save new patient (MOVED from /booking)
// - POST /existing-patient/search - Search existing patient
// - POST /provider/facilities - Get facilities
// - POST /provider/providers - Get providers
// - POST /provider/schedule-timeslot - Get time slots
// - POST /booking/book - Book appointment
//
// DEBUG FEATURES:
// - Console logging for all API calls and responses
// - Debug panels showing request/response data
// - Error handling with detailed messages
// - Step-by-step data validation

import React, { useState, useEffect } from 'react';
import { isAllowedHomepsychProvider, HOMEPSYCH_FACILITY_ID } from './providerConfig';
import {
  DEBUG_MODE,
  MAINTENANCE_MODE,
  API_BASE_URL,
  LOGGING_URL,
  PRACTICE_ID_ENCRYPTED,
  DEFAULT_PRACTICE_ID,
  NEW_PATIENT_VISIT_TYPES,
  EXISTING_PATIENT_VISIT_TYPES,
  LOCATIONS_WITH_COORDS,
} from './appointment/constants';
import { getNearestLocations, getLocationName } from './appointment/locationUtils';
import { createApiClient } from './appointment/api';
import { getProgramType, getProgramColor, mapDaysToNames, formatTimeRange } from './appointment/helpers';
import Step1UserType from './appointment/Step1UserType';
import Step2NewPatient from './appointment/Step2NewPatient';
import Step2ExistingPatient from './appointment/Step2ExistingPatient';
import Step3SchedulingMethod from './appointment/Step3SchedulingMethod';
import Step4FindNextAvailable from './appointment/Step4FindNextAvailable';
import Step4ManualSelection from './appointment/Step4ManualSelection';
import Step5Review from './appointment/Step5Review';
import DebugPanel from './appointment/DebugPanel';

function AppointmentForm({ onClose, encryptionKey }) {
  // DEBUG_MODE, MAINTENANCE_MODE, API_BASE_URL, LOGGING_URL, LOCATIONS_WITH_COORDS
  // are imported from ./appointment/constants

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [userType, setUserType] = useState(''); // 'new' or 'existing'
  const [debugMode, setDebugMode] = useState(false); // Debug panels hidden by default (toggle at bottom)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [nearestLocations, setNearestLocations] = useState([]); // Store filtered locations based on zipcode
  
  // NEW: Scheduling method selection state
  const [schedulingMethod, setSchedulingMethod] = useState(''); // 'find-next-available' or 'manual-selection'
  
  // NEW: Assigned provider/location for existing patients
  const [assignedProvider, setAssignedProvider] = useState(null); // Store assigned provider data
  const [assignedLocation, setAssignedLocation] = useState(null); // Store assigned location data
  
  // Patient data states
  const [newPatientData, setNewPatientData] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    sex: '',
    email: '',
    mobile_phone: '',
    address: '',
    zipcode: '',
    zip_id: null,
    comments: '',
    practice_id_encrypted: PRACTICE_ID_ENCRYPTED,
    appointment_request_id: '0'
  });
  
  const [existingPatientData, setExistingPatientData] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    practice_id: String(DEFAULT_PRACTICE_ID)
  });
  
  const [existingPatientResult, setExistingPatientResult] = useState(null);
  const [zipcodeData, setZipcodeData] = useState(null);
  const [patientLocationInfo, setPatientLocationInfo] = useState(null); // Store patient's city/zipcode for display
  
  // Visit type states
  const [visitTypes, setVisitTypes] = useState([]);
  const [selectedVisitType, setSelectedVisitType] = useState(String(NEW_PATIENT_VISIT_TYPES[0].id));
  
  // Visit type arrays imported from ./appointment/constants
  const newPatientVisitTypes = NEW_PATIENT_VISIT_TYPES;
  const existingPatientVisitTypes = EXISTING_PATIENT_VISIT_TYPES;
  
  // Booking data states
  const [savedPatientId, setSavedPatientId] = useState(null);
  const [bookingData, setBookingData] = useState({
    practice_id: DEFAULT_PRACTICE_ID,
    patient_id: null,
    user_id: -1,
    facility_id: null,
    resource_id: null,
    schedule_setup_id: null,
    resource_type: '',
    is_flexible: false,
    preferred_date: '',
    preferred_time: '',
    schedule_id: null,
    flexible_date_from: '',
    flexible_date_to: '',
    flexible_time_from: '',
    flexible_time_to: '',
    comments: 'Online appointment booking',
    appointment_status: 1,
    is_telemedicine: false,
    visit_type_id: NEW_PATIENT_VISIT_TYPES[0].id,
    duration: 40,
    is_new_api_patient: true
  });
  
  // Debug and error states
  const [debugLog, setDebugLog] = useState([]);
  const [lastApiResponse, setLastApiResponse] = useState(null);
  const [apiError, setApiError] = useState('');
  
  // Provider filtering state
  const [providers, setProviders] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedVisitDate, setSelectedVisitDate] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Timeslot availability states
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);

  // Find Next Available states
  const [showNextAvailable, setShowNextAvailable] = useState(false);
  const [isLoadingNextAvailable, setIsLoadingNextAvailable] = useState(false);
  const [nextAvailableResults, setNextAvailableResults] = useState([]);
  const [selectedNextAvailableSlot, setSelectedNextAvailableSlot] = useState(null); // Track selected slot for highlighting
  const [searchComplete, setSearchComplete] = useState(false); // Track if search is complete

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // TASK 2 & 4: Custom popup states
  const [showExistingPatientPopup, setShowExistingPatientPopup] = useState(false);
  const [existingPatientCheckResult, setExistingPatientCheckResult] = useState(null);
  const [selectedSearchLocation, setSelectedSearchLocation] = useState(null); // Track which location is being searched
  
  // TASK: Patient not found popup state
  const [showPatientNotFoundPopup, setShowPatientNotFoundPopup] = useState(false);
  const [patientNotFoundData, setPatientNotFoundData] = useState(null);
  
  // TASK: Existing patient found popup state (for existing patient form)
  const [showExistingPatientFoundPopup, setShowExistingPatientFoundPopup] = useState(false);
  
  // TASK 2: Insurance number tracking (separate from comments)
  const [insuranceNumber, setInsuranceNumber] = useState('');
  
  // TASK 3: Preferred language tracking (required field)
  const [preferredLanguage, setPreferredLanguage] = useState('');

  // Use the encryption key passed from parent component (already validated at gate)
  const referralAccessKey = encryptionKey || '';

  // Session-scoped API response cache (cleared on Start Over).
  // Keyed by "${facilityId}:${visitDate}" for providers, "${resourceId}:${scheduleSetupId}:${visitDate}" for slots.
  // useRef keeps stable references across renders without causing re-renders.
  const providerCache = React.useRef(new Map());
  const slotCache = React.useRef(new Map());

  // Debug logging utility
  const addToDebugLog = (action, data, response = null, error = null) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action,
      data,
      response,
      error
    };
    setDebugLog(prev => [...prev, logEntry]);
    if (DEBUG_MODE) {
      console.log(`ðŸ” [DEBUG] ${action}:`, logEntry);
    }
  };

  // API client â€” created once per mount; all endpoint calls go through this.
  // addToDebugLog, setLastApiResponse, setApiError are stable (stable setters / same-ref function).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const api = React.useMemo(
    () => createApiClient(API_BASE_URL, addToDebugLog, setLastApiResponse, setApiError),
    // API_BASE_URL is a module-level constant derived from env/window â€” effectively stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [API_BASE_URL]
  );
  const {
    apiCall,
    lookupZipcode,
    saveNewPatient,
    searchExistingPatient,
    getProviders,
    getTimeSlots,
    bookAppointment,
    testServerConnection,
  } = api;

  // ============================================================================
  // SESSION PERSISTENCE — restore mid-form state after accidental page refresh
  // ============================================================================
  // On mount: attempt to restore critical state so a refresh doesn't force
  // re-entry of data or (worse) a duplicate patient API call.
  // On step advance: persist key state slices to sessionStorage.
  // Cleared on: successful booking, Start Over, or explicit session clear.
  const SESSION_FORM_KEY = 'namhs_form_session';

  // Restore on first mount
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_FORM_KEY);
    if (!saved) return;
    try {
      const {
        savedPatientId: sPid,
        userType: sUserType,
        currentStep: sStep,
        newPatientData: sNpd,
        insuranceNumber: sIns,
        preferredLanguage: sLang,
      } = JSON.parse(saved);

      // Only restore if a patient was already saved (prevents duplicate /save-new-patient calls)
      if (sPid) {
        setSavedPatientId(sPid);
        if (sUserType) setUserType(sUserType);
        if (sNpd) setNewPatientData(prev => ({ ...prev, ...sNpd }));
        if (sIns) setInsuranceNumber(sIns);
        if (sLang) setPreferredLanguage(sLang);
        // Restore to the last step the operator was on (but not Step 4+ — they need to re-select)
        if (sStep && sStep >= 2 && sStep <= 3) setCurrentStep(sStep);
      }
    } catch {
      sessionStorage.removeItem(SESSION_FORM_KEY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every step change (after data is stable)
  useEffect(() => {
    if (!savedPatientId) return; // Only persist once a patient record exists
    const snapshot = {
      savedPatientId,
      userType,
      currentStep,
      newPatientData: {
        first_name: newPatientData.first_name,
        last_name: newPatientData.last_name,
        dob: newPatientData.dob,
        sex: newPatientData.sex,
        email: newPatientData.email,
        mobile_phone: newPatientData.mobile_phone,
        address: newPatientData.address,
        zipcode: newPatientData.zipcode,
        zip_id: newPatientData.zip_id,
        practice_id_encrypted: newPatientData.practice_id_encrypted,
        appointment_request_id: newPatientData.appointment_request_id,
      },
      insuranceNumber,
      preferredLanguage,
    };
    sessionStorage.setItem(SESSION_FORM_KEY, JSON.stringify(snapshot));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, savedPatientId]);

  // TASK 2: Auto-fill new patient form when DEBUG_MODE is enabled
  useEffect(() => {
    if (DEBUG_MODE && userType === 'new' && currentStep === 2) {
      // Auto-fill form with test data
      const autoFillData = {
        first_name: 'Ben (DEBUG-TEST)',
        last_name: 'TEST',
        dob: '01/01/2023',
        sex: '2', // Male
        email: 'byou@namhs.com',
        mobile_phone: '(123) 456-7890',
        address: '123 Test Address',
        zipcode: '96001',
        zip_id: null,
        comments: 'Patient has severe PTSD.',
        practice_id_encrypted: PRACTICE_ID_ENCRYPTED,
        appointment_request_id: '0'
      };
      
      setNewPatientData(autoFillData);
      setInsuranceNumber('123test');
      setPreferredLanguage('English and Chinese');
      
      // Lookup zipcode if needed - use apiCall directly to avoid dependency issues
      apiCall('/patient/zipcode', 'POST', { prefix: '96001' }, 'ZIP Code Lookup (Debug Auto-fill)')
        .then(zipData => {
          if (zipData && zipData.length > 0) {
            setZipcodeData(zipData[0]);
            setNewPatientData(prev => ({
              ...prev,
              zip_id: zipData[0].ZipID
            }));
          }
        })
        .catch(() => {
          // Silently fail - zipcode lookup is optional for debug
        });
      
      if (DEBUG_MODE) {
        console.log('ðŸ” [DEBUG] Auto-filled new patient form with test data');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DEBUG_MODE, userType, currentStep]); // Note: apiCall is stable, no need to include in deps

  // Log initial page access timestamp to server
  useEffect(() => {
    const accessTimestamp = new Date().toISOString();
    const accessTimeLocal = new Date().toLocaleString('en-US', { 
      dateStyle: 'full', 
      timeStyle: 'long' 
    });
    
    // Send log to server terminal
    const logData = {
      event: 'PAGE_ACCESS',
      timestamp: accessTimestamp,
      timestampLocal: accessTimeLocal,
      userAgent: navigator.userAgent,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      currentStep: 1
    };
    
    // Send to server (fire and forget, don't block UI)
    fetch(`${LOGGING_URL}/log/user-activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logData)
    }).catch(err => {
      // Silently fail if logging endpoint doesn't exist
      if (DEBUG_MODE) console.log('Could not send activity log to server:', err.message);
    });
    
    if (DEBUG_MODE) {
      console.log('='.repeat(80));
      console.log('ðŸŒ [PAGE ACCESS] User accessed appointment form');
      console.log(`ðŸ“… Timestamp (ISO): ${accessTimestamp}`);
      console.log(`ðŸ“… Timestamp (Local): ${accessTimeLocal}`);
      console.log(`ðŸ”— API Base URL: ${API_BASE_URL}`);
      console.log(`ðŸ–¥ï¸  User Agent: ${navigator.userAgent}`);
      console.log(`ðŸ“± Screen Size: ${window.innerWidth}x${window.innerHeight}`);
      console.log('='.repeat(80));
    }
    addToDebugLog('Page Access', logData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = runs once on mount
  
  // REMOVED: Lockout useEffect hooks - No longer needed (lockout handled at gate)
  
  // Log step changes to server terminal
  useEffect(() => {
    if (currentStep > 0) {
      const stepNames = {
        1: 'User Type Selection',
        2: 'Patient Information',
        3: 'Provider Selection',
        4: 'Review & Confirm'
      };
      
      const logData = {
        event: 'STEP_CHANGE',
        timestamp: new Date().toISOString(),
        currentStep: currentStep,
        stepName: stepNames[currentStep],
        userType: userType
      };
      
      // Send to server (fire and forget)
      fetch(`${LOGGING_URL}/log/user-activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      }).catch(err => {
        if (DEBUG_MODE) console.log('Could not send activity log to server:', err.message);
      });
      
      if (DEBUG_MODE) {
        console.log(`ðŸ“ [STEP CHANGE] User moved to Step ${currentStep}: ${stepNames[currentStep]}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, userType]); // Run whenever step changes

  // DEBUG: Log detailed review page data when user reaches Step 5
  useEffect(() => {
    if (currentStep === 5 && DEBUG_MODE) {
      console.log('='.repeat(80));
      console.log('ðŸ“º [REVIEW PAGE RENDER] Displaying appointment details to user');
      console.log('='.repeat(80));
      
      // Calculate the display date
      let displayDate = 'Not set';
      if (selectedNextAvailableSlot?.dateDisplay) {
        displayDate = selectedNextAvailableSlot.dateDisplay;
      } else if (selectedVisitDate) {
        const [year, month, day] = selectedVisitDate.split('-');
        const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        displayDate = dateObj.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        });
      }
      
      // Calculate the time range display
      let timeDisplay = 'Not set';
      if (selectedTimeSlot && bookingData.duration) {
        const timeRange = formatTimeRange(selectedTimeSlot, bookingData.duration);
        timeDisplay = timeRange.displayRange;
      }
      
      // Get provider data
      const selectedProviderData = providers.find(p => p.ResourceId.toString() === selectedProvider);
      
      console.log('ðŸ“‹ [WHAT USER SEES]');
      console.log('  Date displayed to user:', displayDate);
      console.log('  Time displayed to user:', timeDisplay);
      console.log('  Location displayed:', getLocationName(selectedLocation));
      console.log('  Provider displayed:', selectedProviderData?.ProviderName || 'Not found');
      console.log('  Duration displayed:', bookingData.duration, 'minutes');
      console.log('');
      console.log('ðŸ” [RAW DATA SOURCES]');
      console.log('  selectedVisitDate (YYYY-MM-DD):', selectedVisitDate);
      console.log('  selectedTimeSlot:', selectedTimeSlot);
      console.log('  selectedLocation:', selectedLocation);
      console.log('  selectedProvider:', selectedProvider);
      console.log('');
      console.log('ðŸ“¦ [BOOKING DATA TO BE SUBMITTED]');
      console.log('  preferred_date (MM/DD/YYYY):', bookingData.preferred_date);
      console.log('  preferred_time:', bookingData.preferred_time);
      console.log('  facility_id:', bookingData.facility_id);
      console.log('  resource_id:', bookingData.resource_id);
      console.log('  schedule_id:', bookingData.schedule_id);
      console.log('  duration:', bookingData.duration);
      console.log('');
      
      if (selectedNextAvailableSlot) {
        console.log('ðŸŽ¯ [ORIGINAL BACKEND DATA] Data from "Find Next Available":');
        console.log('  Backend date (YYYY-MM-DD):', selectedNextAvailableSlot.date);
        console.log('  Backend dateDisplay:', selectedNextAvailableSlot.dateDisplay);
        console.log('  Backend timeSlot:', selectedNextAvailableSlot.timeSlot);
        console.log('  Backend facilityId:', selectedNextAvailableSlot.facilityId);
        console.log('  Backend providerId:', selectedNextAvailableSlot.providerId);
        console.log('  Backend providerName:', selectedNextAvailableSlot.providerName);
        console.log('  Backend scheduleId:', selectedNextAvailableSlot.scheduleId);
        console.log('  Backend duration:', selectedNextAvailableSlot.duration);
        console.log('');
        
        // Final verification
        console.log('âœ… [FINAL VERIFICATION]');
        if (displayDate === selectedNextAvailableSlot.dateDisplay) {
          console.log('  âœ“ Date matches backend data');
        } else {
          console.log('  âœ— Date MISMATCH:');
          console.log('    Backend:', selectedNextAvailableSlot.dateDisplay);
          console.log('    Display:', displayDate);
        }
        
        if (selectedTimeSlot === selectedNextAvailableSlot.timeSlot) {
          console.log('  âœ“ Time matches backend data');
        } else {
          console.log('  âœ— Time MISMATCH:');
          console.log('    Backend:', selectedNextAvailableSlot.timeSlot);
          console.log('    Display:', selectedTimeSlot);
        }
        
        if (selectedLocation === selectedNextAvailableSlot.facilityId) {
          console.log('  âœ“ Location matches backend data');
        } else {
          console.log('  âœ— Location MISMATCH:');
          console.log('    Backend:', selectedNextAvailableSlot.facilityId);
          console.log('    Display:', selectedLocation);
        }
        
        if (selectedProvider === selectedNextAvailableSlot.providerId) {
          console.log('  âœ“ Provider matches backend data');
        } else {
          console.log('  âœ— Provider MISMATCH:');
          console.log('    Backend:', selectedNextAvailableSlot.providerId);
          console.log('    Display:', selectedProvider);
        }
      }
      
      console.log('='.repeat(80));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedVisitDate, selectedTimeSlot, selectedLocation, selectedProvider, selectedNextAvailableSlot, bookingData, providers]);

  // apiCall, lookupZipcode, saveNewPatient, searchExistingPatient, getProviders,
  // getTimeSlots, bookAppointment, testServerConnection are all provided by api (above).
  // Encryption key validation happens at EncryptionKeyGate before the form is mounted.


  // Utility function to parse zipcode from address string
  const parseZipcodeFromAddress = (address) => {
    if (!address) return null;
    // Look for 5-digit zipcode at the end of the address
    const zipcodeMatch = address.match(/\b(\d{5})(?:-\d{4})?\b/);
    return zipcodeMatch ? zipcodeMatch[1] : null;
  };

  // TASK 6: Validation Functions
  const validateDateOfBirth = (dob) => {
    // Check if DOB is empty or invalid format
    if (!dob || dob.trim() === '') {
      return { valid: false, error: 'Date of birth is required' };
    }
    
    // Check if DOB is 00/00/0000 or similar invalid pattern
    if (dob === '00/00/0000' || dob.match(/^0+\/0+\/0+$/)) {
      return { valid: false, error: 'Please enter a valid date of birth (not 00/00/0000)' };
    }
    
    // Check format MM/DD/YYYY
    const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!datePattern.test(dob)) {
      return { valid: false, error: 'Please enter date in MM/DD/YYYY format (e.g., 01/15/1990)' };
    }
    
    // Validate actual date values
    const [month, day, year] = dob.split('/').map(Number);
    
    if (month === 0 || day === 0 || year === 0) {
      return { valid: false, error: 'Date cannot contain zeros (00/00/0000)' };
    }
    
    if (month < 1 || month > 12) {
      return { valid: false, error: 'Month must be between 01 and 12' };
    }
    
    if (day < 1 || day > 31) {
      return { valid: false, error: 'Day must be between 01 and 31' };
    }
    
    if (year < 1900 || year > new Date().getFullYear()) {
      return { valid: false, error: `Year must be between 1900 and ${new Date().getFullYear()}` };
    }
    
    // Validate the date is actually valid (e.g., not 02/31/2020)
    const date = new Date(year, month - 1, day);
    if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
      return { valid: false, error: 'Please enter a valid date (e.g., 01/15/1990)' };
    }
    
    return { valid: true, error: null };
  };

  const validatePhoneNumber = (phone) => {
    // Check if phone is empty
    if (!phone || phone.trim() === '') {
      return { valid: false, error: 'Phone number is required' };
    }
    
    // Remove common formatting characters
    const cleanPhone = phone.replace(/[\s\-().]/g, '');
    
    // Check if phone contains any letters
    if (/[a-zA-Z]/.test(cleanPhone)) {
      return { valid: false, error: 'Phone number cannot contain letters' };
    }
    
    // Check if phone has at least 10 digits
    if (cleanPhone.length < 10) {
      return { valid: false, error: 'Phone number must have at least 10 digits' };
    }
    
    // Check if phone is all zeros
    if (/^0+$/.test(cleanPhone)) {
      return { valid: false, error: 'Please enter a valid phone number (not all zeros)' };
    }
    
    return { valid: true, error: null };
  };

  // TASK 3: Calculate distance between two coordinates using Haversine formula
  // calculateDistance and getNearestLocations imported from ./appointment/locationUtils
  // getNearestLocations wrapper: passes lookupZipcode and addToDebugLog as dependencies
  const getNearestLocationsForPatient = (patientZipcode) =>
    getNearestLocations(patientZipcode, lookupZipcode, addToDebugLog);


  // Initialize visit types based on user type
  React.useEffect(() => {
    if (userType === 'new') {
      setVisitTypes(newPatientVisitTypes);
      setSelectedVisitType(String(NEW_PATIENT_VISIT_TYPES[0].id));
      setBookingData(prev => ({
        ...prev,
        visit_type_id: NEW_PATIENT_VISIT_TYPES[0].id,
        duration: NEW_PATIENT_VISIT_TYPES[0].duration
      }));
    } else if (userType === 'existing') {
      setVisitTypes(existingPatientVisitTypes);
      setSelectedVisitType(String(EXISTING_PATIENT_VISIT_TYPES[0].id));
      setBookingData(prev => ({
        ...prev,
        visit_type_id: EXISTING_PATIENT_VISIT_TYPES[0].id,
        duration: EXISTING_PATIENT_VISIT_TYPES[0].duration
      }));
    }
  }, [userType, newPatientVisitTypes, existingPatientVisitTypes]);



  // getProgramType, getProgramColor, mapDaysToNames, formatTimeRange
  // imported from ./appointment/helpers


  // getLocationName imported from ./appointment/locationUtils
  // formatTimeRange imported from ./appointment/helpers



  // Load available time slots for selected provider and date
  const loadTimeSlots = async (providerId, scheduleSetupId, visitDate) => {
    if (!providerId || !scheduleSetupId || !visitDate) {
      setAvailableTimeSlots([]);
      return;
    }

    setIsLoadingTimeSlots(true);
    setSelectedTimeSlot('');

    try {
      // Convert date from YYYY-MM-DD to MM/DD/YYYY format for API
      const dateParts = visitDate.split('-');
      const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;

      // Use new API endpoint with updated parameters
      const scheduleData = {
        practice_id: DEFAULT_PRACTICE_ID,
        facility_id: parseInt(selectedLocation),
        resource_id: parseInt(providerId),
        schedule_setup_id: parseInt(scheduleSetupId),
        encounter_type: parseInt(selectedVisitType),
        visit_date: formattedDate,
        duration: bookingData.duration,
        is_new_patient: userType === 'new'
      };

      const slotCacheKey = `${providerId}:${scheduleSetupId}:${formattedDate}`;
      let data;
      if (slotCache.current.has(slotCacheKey)) {
        data = slotCache.current.get(slotCacheKey);
        if (DEBUG_MODE) console.log(`[CACHE HIT] Time slots for ${slotCacheKey}`);
      } else {
        data = await getTimeSlots(scheduleData);
        slotCache.current.set(slotCacheKey, data);
      }
      // Remove duplicates based on ScheduleID and Time
      const uniqueTimeSlots = (data.schedulerSlot || []).filter((slot, index, self) =>
        index === self.findIndex((s) => s.ScheduleID === slot.ScheduleID && s.Time === slot.Time)
      );
      setAvailableTimeSlots(uniqueTimeSlots);
      addToDebugLog('Time Slots Loaded', scheduleData, { ...data, uniqueSlots: uniqueTimeSlots.length, originalSlots: (data.schedulerSlot || []).length });
      
    } catch (error) {
      if (DEBUG_MODE) {
        console.error('Error loading time slots:', error);
      }
      addToDebugLog('Time Slots Error', { providerId, scheduleSetupId, visitDate }, null, error.message);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  // Find Next Available - TASK 1: Updated to 10 business days with dynamic/streaming results
  const handleFindNextAvailable = async (specificLocation = null) => {
    setIsLoadingNextAvailable(true);
    setShowNextAvailable(true);
    setNextAvailableResults([]);
    setSelectedNextAvailableSlot(null); // Clear any previous selection
    setSelectedSearchLocation(specificLocation); // Track which location is being searched
    setSearchComplete(false); // Reset search complete status
    
    const searchStartTime = performance.now();
    let totalApiCalls = 0;
    
    try {
      // OPTIMIZATION 1: Determine locations to search - sort by distance (closest first)
      let locationsToSearch;
      
      if (specificLocation) {
        // Search only the specific location
        locationsToSearch = [specificLocation];
        if (DEBUG_MODE) {
          console.log('ðŸŽ¯ [FIND NEXT] Searching specific location:', specificLocation);
        }
      } else {
        // Use filtered locations based on patient's zipcode (within 30 miles)
        // Sort by distance to prioritize closest locations
        locationsToSearch = nearestLocations.length > 0 
          ? nearestLocations
              .map(loc => ({ id: loc.id, name: loc.name, distance: loc.distance }))
              .sort((a, b) => (a.distance || 999) - (b.distance || 999)) // Closest first
          : [
              { id: '210', name: 'Eureka - NAMHS' },
              { id: '211', name: 'Fairfield - NAMHS' },
              { id: '212', name: 'Homepsych - NAMHS' },
              { id: '216', name: 'Monterey - NAMHS' },
              { id: '214', name: 'Red Bluff - NAMHS' },
              { id: '199', name: 'Redding - NAMHS' },
              { id: '217', name: 'Salinas - NAMHS' },
              { id: '215', name: 'Hollister - NAMHS' }
            ];
        
        if (DEBUG_MODE) {
          console.log('ðŸŽ¯ [FIND NEXT] Locations prioritized by distance:', locationsToSearch);
        }
      }
      
      // OPTIMIZATION 2: Generate business days (Monday-Friday only)
      const maxBusinessDaysToSearch = 10;
      const today = new Date();
      
      const businessDays = [];
      let currentDate = new Date(today);
      let daysChecked = 0;
      const maxDaysToCheck = 21; // Safety limit: check up to 3 weeks
      
      while (businessDays.length < maxBusinessDaysToSearch && daysChecked < maxDaysToCheck) {
        const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
        
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          businessDays.push(new Date(currentDate));
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
        daysChecked++;
      }
      
      if (DEBUG_MODE) {
        console.log(`ðŸš€ [FIND NEXT] Starting OPTIMIZED search: ${businessDays.length} business days, ${locationsToSearch.length} locations`);
        console.log(`ðŸš€ [FIND NEXT] Strategy: Search nearest dates first, early termination enabled`);
      }
      
      // Results tracking
      const resultsMap = new Map();
      let searchTerminated = false;
      
      // Helper function to update results state dynamically
      const updateResults = () => {
        const sortedResults = Array.from(resultsMap.values())
          .filter(day => day.locations.length > 0)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, maxBusinessDaysToSearch);
        
        setNextAvailableResults([...sortedResults]);
        return sortedResults;
      };
      
      // OPTIMIZATION 3: Early termination check - stop if we have enough results
      const shouldTerminateSearch = () => {
        const currentResults = Array.from(resultsMap.values());
        const daysWithResults = currentResults.length;
        const totalSlots = currentResults.reduce((sum, day) => {
          return sum + day.locations.reduce((locSum, loc) => {
            return locSum + loc.providers.reduce((provSum, prov) => provSum + prov.timeSlots.length, 0);
          }, 0);
        }, 0);
        
        // Terminate if we have:
        // - At least 3 days with availability AND
        // - At least 15 total time slots across all days
        const shouldTerminate = daysWithResults >= 3 && totalSlots >= 15;
        
        if (shouldTerminate && DEBUG_MODE) {
          console.log(`âš¡ [OPTIMIZATION] Early termination: ${daysWithResults} days, ${totalSlots} slots found`);
        }
        
        return shouldTerminate;
      };
      
      // OPTIMIZATION 4 (PARALLEL): Extract per-location search into a named function so all
      // locations can be launched concurrently via Promise.allSettled.
      // Each location searches dates in batches of 3. The shared resultsMap and
      // searchTerminated flag coordinate early termination across all parallel searches.
      // JavaScript's single-threaded event loop ensures atomic reads/writes between awaits.
      const searchSingleLocation = async (location) => {
        if (DEBUG_MODE) {
          console.log(`[FIND NEXT] Searching location: ${location.name}${location.distance ? ` (${location.distance.toFixed(1)} mi)` : ''}`);
        }

        const CONCURRENT_DATE_SEARCHES = 3;

        for (let i = 0; i < businessDays.length; i += CONCURRENT_DATE_SEARCHES) {
          if (searchTerminated) break;

          const dateBatch = businessDays.slice(i, i + CONCURRENT_DATE_SEARCHES);

          // eslint-disable-next-line no-loop-func
          await Promise.allSettled(
            dateBatch.map(async (searchDate) => {
              if (searchTerminated) return;

              const dateKey = searchDate.toISOString().split('T')[0];
              const [year, month, day] = dateKey.split('-').map(Number);
              const displayDate = new Date(year, month - 1, day, 12, 0, 0);
              const dateDisplay = displayDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              });

              try {
                const dateParts = dateKey.split('-');
                const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;

                const filterData = {
                  practice_id: DEFAULT_PRACTICE_ID,
                  facility_id: parseInt(location.id),
                  visit_date: formattedDate,
                  gender_ids: '',
                  language_ids: '',
                  service_offered_ids: '',
                  credential_ids: '',
                  patient_id: savedPatientId || (existingPatientResult ? existingPatientResult.PatientId : 0)
                };

                totalApiCalls++;
                const allProviders = await getProviders(filterData);

                const filteredProviders = allProviders.filter(provider => {
                  const programType = getProgramType(provider.ProviderName);
                  const isValidType = programType === 'Medical' || programType === 'Psychiatric';
                  if (parseInt(location.id) === HOMEPSYCH_FACILITY_ID) {
                    return isValidType && isAllowedHomepsychProvider(provider.ProviderName);
                  }
                  return isValidType;
                });

                if (filteredProviders.length === 0) return;

                if (DEBUG_MODE) {
                  console.log(`   [OK] ${dateDisplay}: ${filteredProviders.length} providers at ${location.name}`);
                }

                const CONCURRENT_PROVIDER_FETCHES = 5;
                const providersWithSlots = [];

                for (let p = 0; p < filteredProviders.length; p += CONCURRENT_PROVIDER_FETCHES) {
                  if (searchTerminated) break;

                  const providerBatch = filteredProviders.slice(p, p + CONCURRENT_PROVIDER_FETCHES);

                  // eslint-disable-next-line no-loop-func
                  const batchResults = await Promise.allSettled(
                    providerBatch.map(async (provider) => {
                      try {
                        const scheduleData = {
                          practice_id: DEFAULT_PRACTICE_ID,
                          facility_id: parseInt(location.id),
                          resource_id: provider.ResourceId,
                          schedule_setup_id: provider.ScheduleSetupID,
                          encounter_type: parseInt(selectedVisitType),
                          visit_date: formattedDate,
                          duration: bookingData.duration,
                          is_new_patient: userType === 'new'
                        };

                        totalApiCalls++;
                        const slotsData = await getTimeSlots(scheduleData);

                        if (DEBUG_MODE && slotsData && slotsData.schedulerSlot && slotsData.schedulerSlot.length > 0) {
                          console.log(`[API] Slots for ${provider.ProviderName} on ${formattedDate} at ${location.name}:`, {
                            rawCount: slotsData.schedulerSlot.length,
                            available: slotsData.schedulerSlot.filter(s => s.IsAvailable === 1 && !s.IsDisabled).length
                          });
                        }

                        const availableSlots = (slotsData.schedulerSlot || [])
                          .filter(slot => slot.IsAvailable === 1 && !slot.IsDisabled)
                          .map(slot => ({
                            time: slot.Time,
                            scheduleId: slot.ScheduleID,
                            duration: slot.Duration
                          }));

                        if (availableSlots.length > 0) {
                          const morningSlots = availableSlots.filter(s => {
                            const [t, per] = s.time.split(' ');
                            const h = parseInt(t.split(':')[0]);
                            return per === 'AM' || (per === 'PM' && h === 12);
                          }).slice(0, 5);
                          const afternoonSlots = availableSlots.filter(s => {
                            const [t, per] = s.time.split(' ');
                            const h = parseInt(t.split(':')[0]);
                            return per === 'PM' && h !== 12;
                          }).slice(0, 5);
                          return {
                            id: provider.ResourceId.toString(),
                            name: provider.ProviderName,
                            scheduleSetupId: provider.ScheduleSetupID.toString(),
                            scheduleId: provider.ScheduleID,
                            timeSlots: [...morningSlots, ...afternoonSlots]
                          };
                        }
                        return null;
                      } catch {
                        return null;
                      }
                    })
                  );

                  batchResults
                    .filter(result => result.status === 'fulfilled' && result.value)
                    .forEach(result => providersWithSlots.push(result.value));
                }

                if (providersWithSlots.length > 0) {
                  // Merge into shared resultsMap — safe because JS is single-threaded
                  // between awaits; the has/set/get/push sequence has no awaits.
                  if (!resultsMap.has(dateKey)) {
                    resultsMap.set(dateKey, {
                      date: dateKey,
                      dateDisplay: dateDisplay,
                      locations: []
                    });
                  }

                  const dayResult = resultsMap.get(dateKey);
                  dayResult.locations.push({
                    date: dateKey,
                    dateDisplay: dateDisplay,
                    facilityId: location.id,
                    name: location.name,
                    providers: providersWithSlots
                  });

                  if (DEBUG_MODE) {
                    console.log(`   [+] ${providersWithSlots.length} providers from ${location.name} on ${dateDisplay}`);
                  }

                  updateResults();

                  if (shouldTerminateSearch()) {
                    searchTerminated = true;
                    if (DEBUG_MODE) {
                      console.log('[OPTIMIZATION] Sufficient results found, stopping all location searches early');
                    }
                  }
                }
              } catch (error) {
                if (DEBUG_MODE) {
                  console.warn(`   [ERR] Error processing ${location.name} on ${dateKey}:`, error);
                }
              }
            })
          );
        }
      };

      // Run all location searches in parallel for maximum speed.
      // Previously sequential (one location at a time); now all fire concurrently.
      // Each location does its own date batching (3 at a time). With multiple locations,
      // this cuts total search time significantly compared to the sequential loop.
      await Promise.allSettled(
        locationsToSearch.map(location => searchSingleLocation(location))
      );
      
      const searchEndTime = performance.now();
      const searchDurationSeconds = ((searchEndTime - searchStartTime) / 1000).toFixed(2);
      
      // Final update
      updateResults();
      setSearchComplete(true);
      
      if (DEBUG_MODE) {
        console.log(`ðŸŽ‰ [FIND NEXT] Search completed in ${searchDurationSeconds}s${searchTerminated ? ' (early termination)' : ''}`);
        console.log(`ðŸ“Š [FIND NEXT] Total API calls: ${totalApiCalls} (saved ${Math.max(0, locationsToSearch.length * businessDays.length * 8 - totalApiCalls)} calls)`);
        console.log(`ðŸ“… [FIND NEXT] Days with availability: ${resultsMap.size}`);
      }
      
      addToDebugLog('Find Next Available Results (OPTIMIZED)', { 
        searchStrategy: 'Prioritized by location distance, early termination enabled',
        businessDaysSearched: businessDays.length,
        locationsSearched: locationsToSearch.length,
        daysWithAvailability: resultsMap.size,
        totalApiCalls: totalApiCalls,
        searchDurationSeconds: searchDurationSeconds,
        earlyTermination: searchTerminated,
        estimatedCallsSaved: searchTerminated ? Math.max(0, locationsToSearch.length * businessDays.length * 8 - totalApiCalls) : 0
      }, Array.from(resultsMap.values()));
      
      if (resultsMap.size === 0) {
        setApiError('No available appointments found in the next 10 business days. Please try Manual Date Selection below or contact the office.');
      }
    } catch (error) {
      if (DEBUG_MODE) {
        console.error('Error finding next available appointments:', error);
      }
      setApiError(`Failed to find available appointments: ${error.message}`);
      setSearchComplete(true);
    } finally {
      setIsLoadingNextAvailable(false);
    }
  };

  // Handle selection from next available results
  const handleSelectNextAvailableSlot = (date, dateDisplay, facilityId, provider, timeSlot, scheduleId, slotDuration) => {
    // Fill in the form with selected values
    setSelectedLocation(facilityId);
    setSelectedProvider(provider.id);
    
    // Convert date string to YYYY-MM-DD format for the date input
    const dateObj = new Date(date);
    const formattedDate = dateObj.toISOString().split('T')[0];
    setSelectedVisitDate(formattedDate);
    setSelectedTimeSlot(timeSlot);
    
    // Update booking data
    const dateParts = date.split('-');
    const apiFormattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
    
    // Use the actual duration from the selected time slot
    const actualDuration = slotDuration || bookingData.duration;
    
    const updatedBookingData = {
      ...bookingData,
      facility_id: parseInt(facilityId),
      resource_id: parseInt(provider.id),
      schedule_setup_id: parseInt(provider.scheduleSetupId),
      schedule_id: scheduleId || null,
      preferred_date: apiFormattedDate,
      preferred_time: timeSlot,
      duration: actualDuration
    };
    
    setBookingData(updatedBookingData);
    
    // Track selected slot for highlighting (keep window visible)
    const selectedSlotData = {
      date,
      dateDisplay, // Store the formatted date display string
      facilityId,
      providerId: provider.id,
      timeSlot,
      scheduleId,
      providerName: provider.name,
      duration: actualDuration // Store duration for display
    };
    
    setSelectedNextAvailableSlot(selectedSlotData);
    
    // DEBUG: Enhanced logging for slot selection
    if (DEBUG_MODE) {
      console.log('='.repeat(80));
      console.log('âœ… [SLOT SELECTED] User clicked on time slot in Find Next Available');
      console.log('='.repeat(80));
      console.log('ðŸ“… [SLOT DATA] Raw data passed to this function:');
      console.log('  - Parameter "date" (YYYY-MM-DD):', date);
      console.log('  - Parameter "dateDisplay":', dateDisplay);
      console.log('  - Parameter "timeSlot":', timeSlot);
      console.log('  - Parameter "scheduleId":', scheduleId);
      console.log('  - Parameter "slotDuration":', slotDuration);
      console.log('  - Calculated "actualDuration":', actualDuration, 'minutes');
      console.log('');
      console.log('ðŸ” [VERIFICATION] Does this time belong to this date?');
      console.log('  - Date we\'re displaying:', dateDisplay);
      console.log('  - Time slot shown:', timeSlot);
      console.log('  - Expected: These should match the day user clicked on');
      console.log('');
      console.log('ðŸ¥ [LOCATION & PROVIDER] Selected details:');
      console.log('  - Facility ID:', facilityId);
      console.log('  - Provider ID:', provider.id);
      console.log('  - Provider Name:', provider.name);
      console.log('  - Schedule Setup ID:', provider.scheduleSetupId);
      console.log('');
      console.log('ðŸ’¾ [DATA BEING SET] Updated booking data:');
      console.log('  - preferred_date (API format MM/DD/YYYY):', apiFormattedDate);
      console.log('  - preferred_time:', timeSlot);
      console.log('  - facility_id:', parseInt(facilityId));
      console.log('  - resource_id:', parseInt(provider.id));
      console.log('  - schedule_setup_id:', parseInt(provider.scheduleSetupId));
      console.log('  - schedule_id:', scheduleId);
      console.log('  - duration:', actualDuration);
      console.log('');
      console.log('ðŸ“‹ [FULL BOOKING DATA] Complete booking data object:');
      console.log(JSON.stringify(updatedBookingData, null, 2));
      console.log('='.repeat(80));
    }
    
    addToDebugLog('Next Available Slot Selected', {
      rawBackendData: {
        date,
        dateDisplay,
        timeSlot,
        scheduleId,
        duration: actualDuration
      },
      locationProvider: {
        facilityId,
        providerId: provider.id,
        providerName: provider.name,
        scheduleSetupId: provider.scheduleSetupId
      },
      bookingDataSet: updatedBookingData,
      selectedSlotTracking: selectedSlotData
    });
    
    // Auto-scroll to confirmation box after a short delay to allow state update
    setTimeout(() => {
      const confirmationElement = document.querySelector('.appointment-confirmation-box');
      if (confirmationElement) {
        confirmationElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };


  // Form handlers for multi-step form
  const handleNewPatientChange = async (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    
    if (name === 'first_name' || name === 'last_name') {
      // Auto-capitalize first letter
      formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    } else if (name === 'mobile_phone') {
      // Format phone number
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length >= 10) {
        formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      } else if (cleaned.length > 6) {
        formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      } else if (cleaned.length > 3) {
        formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else if (cleaned.length > 0) {
        formattedValue = `(${cleaned}`;
      }
    } else if (name === 'dob') {
      // Date picker returns YYYY-MM-DD; convert to MM/DD/YYYY for API/validation
      if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-');
        formattedValue = `${m}/${d}/${y}`;
      }
    }
    
    setNewPatientData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    
    // Handle zipcode lookup for separate zipcode field
    if (name === 'zipcode' && formattedValue.length === 5) {
      // Lookup zipcode data when zipcode is entered
      try {
        const zipData = await lookupZipcode(formattedValue);
        if (zipData && zipData.length > 0) {
          setZipcodeData(zipData[0]);
          setNewPatientData(prev => ({
            ...prev,
            zip_id: zipData[0].ZipID
          }));
          addToDebugLog('New Patient Zipcode Lookup', { zipcode: formattedValue }, zipData[0]);
        }
      } catch (zipError) {
        if (DEBUG_MODE) {
          console.warn('Failed to lookup zipcode for new patient:', zipError);
        }
      }
    }
  };

  const handleExistingPatientChange = (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === 'first_name' || name === 'last_name') {
      formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    } else if (name === 'dob') {
      // Date picker returns YYYY-MM-DD; convert to MM/DD/YYYY for API/validation
      if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-');
        formattedValue = `${m}/${d}/${y}`;
      }
    }
    
    setExistingPatientData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  // Handle visit type selection
  const handleVisitTypeChange = (e) => {
    const visitTypeId = parseInt(e.target.value);
    setSelectedVisitType(visitTypeId.toString());
    
    // Find the selected visit type to get its duration
    const selectedVisitTypeObj = visitTypes.find(vt => vt.id === visitTypeId);
    const duration = selectedVisitTypeObj?.duration || 40; // Default to 40 if not found
    
    // Update booking data with new visit type and duration
    setBookingData(prev => ({
      ...prev,
      visit_type_id: visitTypeId,
      duration: duration
    }));
    
    addToDebugLog('Visit Type Selected', { 
      visitTypeId, 
      visitTypeName: selectedVisitTypeObj?.name,
      duration: duration
    });
  };

  // Step navigation handlers
  const handleNextStep = () => {
    const nextStep = currentStep + 1;
    
    // DEBUG: Log when navigating to review page (Step 5)
    if (nextStep === 5 && DEBUG_MODE) {
      console.log('='.repeat(80));
      console.log('ðŸ“‹ [REVIEW PAGE] User clicked "Review Appointment" - Entering Step 5');
      console.log('='.repeat(80));
      console.log('');
      console.log('ðŸ” [DATA VERIFICATION] Checking if displayed data matches backend data:');
      console.log('');
      
      // Check if this is from Find Next Available or Manual Selection
      if (selectedNextAvailableSlot) {
        console.log('ðŸ“Œ [SOURCE] Appointment selected from "Find Next Available"');
        console.log('');
        console.log('ðŸ“… [DATE COMPARISON]');
        console.log('  Backend Date (stored in selectedNextAvailableSlot):');
        console.log('    - Raw date (YYYY-MM-DD):', selectedNextAvailableSlot.date);
        console.log('    - Display date:', selectedNextAvailableSlot.dateDisplay);
        console.log('  Frontend Display Date (what should be shown):');
        if (selectedVisitDate) {
          const [year, month, day] = selectedVisitDate.split('-');
          const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          const displayDate = dateObj.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          });
          console.log('    - Formatted date:', displayDate);
        }
        console.log('  Booking Data Date (sent to API):');
        console.log('    - preferred_date (MM/DD/YYYY):', bookingData.preferred_date);
        console.log('');
        console.log('â° [TIME COMPARISON]');
        console.log('  Backend Time (from slot selection):', selectedNextAvailableSlot.timeSlot);
        console.log('  Frontend Time (what should be shown):', selectedTimeSlot);
        console.log('  Booking Data Time (sent to API):', bookingData.preferred_time);
        console.log('  Duration:', selectedNextAvailableSlot.duration || bookingData.duration, 'minutes');
        console.log('');
        console.log('ðŸ¥ [LOCATION & PROVIDER]');
        console.log('  Backend Facility ID:', selectedNextAvailableSlot.facilityId);
        console.log('  Frontend Facility ID:', selectedLocation);
        console.log('  Booking Data Facility ID:', bookingData.facility_id);
        console.log('');
        console.log('  Backend Provider:', {
          id: selectedNextAvailableSlot.providerId,
          name: selectedNextAvailableSlot.providerName
        });
        console.log('  Frontend Provider ID:', selectedProvider);
        console.log('  Booking Data Resource ID:', bookingData.resource_id);
        console.log('');
        console.log('ðŸ”¢ [SCHEDULE IDs]');
        console.log('  Backend Schedule ID:', selectedNextAvailableSlot.scheduleId);
        console.log('  Booking Data Schedule ID:', bookingData.schedule_id);
        console.log('  Booking Data Schedule Setup ID:', bookingData.schedule_setup_id);
        console.log('');
        
        // Verify if dates match
        if (selectedNextAvailableSlot.dateDisplay && selectedVisitDate) {
          const [year, month, day] = selectedVisitDate.split('-');
          const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          const frontendDisplay = dateObj.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          });
          const backendDisplay = selectedNextAvailableSlot.dateDisplay;
          
          if (frontendDisplay === backendDisplay) {
            console.log('âœ… [VERIFICATION SUCCESS] Dates match perfectly!');
            console.log('   Backend:', backendDisplay);
            console.log('   Frontend:', frontendDisplay);
          } else {
            console.log('âš ï¸ [VERIFICATION WARNING] Date mismatch detected!');
            console.log('   Backend:', backendDisplay);
            console.log('   Frontend:', frontendDisplay);
          }
        }
        console.log('');
      } else {
        console.log('ðŸ“Œ [SOURCE] Appointment selected from "Manual Selection"');
        console.log('');
        console.log('ðŸ“… [DATE]');
        console.log('  Selected Visit Date (YYYY-MM-DD):', selectedVisitDate);
        console.log('  Booking Data preferred_date (MM/DD/YYYY):', bookingData.preferred_date);
        console.log('');
        console.log('â° [TIME]');
        console.log('  Selected Time Slot:', selectedTimeSlot);
        console.log('  Booking Data preferred_time:', bookingData.preferred_time);
        console.log('  Duration:', bookingData.duration, 'minutes');
        console.log('');
      }
      
      console.log('ðŸ’¾ [COMPLETE BOOKING DATA] Full booking data to be submitted:');
      console.log(JSON.stringify(bookingData, null, 2));
      console.log('');
      console.log('ðŸ‘¤ [PATIENT DATA]');
      if (userType === 'new') {
        console.log('  Patient Type: New Patient');
        console.log('  Patient ID:', savedPatientId);
        console.log('  Name:', newPatientData.first_name, newPatientData.last_name);
        console.log('  DOB:', newPatientData.dob);
      } else {
        console.log('  Patient Type: Existing Patient');
        console.log('  Patient ID:', existingPatientResult?.PatientId || savedPatientId);
        console.log('  Name:', existingPatientResult?.FirstName, existingPatientResult?.LastName);
        console.log('  MRN:', existingPatientResult?.MRNNumber);
      }
      console.log('='.repeat(80));
    }
    
    setCurrentStep(nextStep);
    
    // Add to debug log for UI panel
    if (nextStep === 5) {
      addToDebugLog('Navigating to Review Page', {
        currentBookingData: bookingData,
        selectedSlotData: selectedNextAvailableSlot,
        formState: {
          selectedLocation,
          selectedProvider,
          selectedVisitDate,
          selectedTimeSlot
        },
        patientData: userType === 'new' ? {
          type: 'new',
          patientId: savedPatientId,
          name: `${newPatientData.first_name} ${newPatientData.last_name}`
        } : {
          type: 'existing',
          patientId: existingPatientResult?.PatientId || savedPatientId,
          name: `${existingPatientResult?.FirstName} ${existingPatientResult?.LastName}`
        }
      });
    }
    
    // Auto-focus to top when navigating steps
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handlePrevStep = () => {
    // Clear scheduling data when going back from Step 4 (scheduling)
    if (currentStep === 4) {
      setSelectedLocation('');
      setSelectedProvider('');
      setSelectedVisitDate('');
      setSelectedTimeSlot('');
      setProviders([]);
      setAvailableTimeSlots([]);
      setShowNextAvailable(false);
      setNextAvailableResults([]);
      setSelectedNextAvailableSlot(null);
      setSearchComplete(false); // Reset search complete status
      
      addToDebugLog('Scheduling Data Cleared', { reason: 'User navigated back from Step 4' });
    }
    
    // Clear scheduling method when going back from Step 3 (method selection)
    if (currentStep === 3) {
      setSchedulingMethod('');
      
      // TASK 1: Clear comments to prevent duplication when going back to new patient page
      setNewPatientData(prev => ({
        ...prev,
        comments: ''
      }));
      
      addToDebugLog('Scheduling Method Cleared', { reason: 'User navigated back from Step 3' });
    }
    
    setCurrentStep(prev => prev - 1);
    // Auto-focus to top when navigating steps
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleStartOver = () => {
    if (window.confirm('Are you sure you want to start over? All entered data will be lost.')) {
      setCurrentStep(1);
      setUserType('');
      setSavedPatientId(null);
      setExistingPatientResult(null);
      setNewPatientData({
        first_name: '',
        last_name: '',
        dob: '',
        sex: '',
        email: '',
        mobile_phone: '',
        address: '',
        zipcode: '',
        zip_id: null,
        comments: '',
        practice_id_encrypted: PRACTICE_ID_ENCRYPTED,
        appointment_request_id: '0'
      });
      setExistingPatientData({
        first_name: '',
        last_name: '',
        dob: '',
        practice_id: String(DEFAULT_PRACTICE_ID)
      });
      setSelectedLocation('');
      setSelectedProvider('');
      setSelectedVisitDate('');
      setAvailableTimeSlots([]);
      setSelectedTimeSlot('');
      setSelectedVisitType(userType === 'new' ? '1067' : '1070'); // Reset to appropriate default
      // Clear Next Available states
      setShowNextAvailable(false);
      setNextAvailableResults([]);
      setSelectedNextAvailableSlot(null);
      setSearchComplete(false); // Reset search complete status
      // TASK 2 & 3: Reset insurance number and preferred language
      setInsuranceNumber('');
      setPreferredLanguage('');
      // NEW: Reset scheduling method and assigned provider/location
      setSchedulingMethod('');
      setAssignedProvider(null);
      setAssignedLocation(null);
      setApiError('');
      providerCache.current.clear();
      slotCache.current.clear();
      sessionStorage.removeItem(SESSION_FORM_KEY);
      addToDebugLog('Form Reset', { action: 'start_over' });
    }
  };

  const handleUserTypeSelection = (type) => {
    setUserType(type);
    addToDebugLog('User Type Selected', { userType: type });
    handleNextStep();
  };

  // TASK 2: Extracted function to actually save the new patient (without existing patient check)
  const saveNewPatientAndProceed = async () => {
    try {
      // First, lookup zipcode if we have one
      if (newPatientData.zipcode && !newPatientData.zip_id) {
        const zipData = await lookupZipcode(newPatientData.zipcode);
        if (zipData && zipData.length > 0) {
          setNewPatientData(prev => ({
            ...prev,
            zip_id: zipData[0].ZipID
          }));
          addToDebugLog('Zipcode Lookup Success', { zipcode: newPatientData.zipcode }, zipData[0]);
        }
      }

      // TASK 2 & 3: Build combined comments with insurance number and preferred language
      let combinedComments = '';
      if (insuranceNumber && insuranceNumber.trim() !== '') {
        // Remove any "Insurance #" or "insurance#" prefix if user entered it
        const cleanInsuranceNumber = insuranceNumber.replace(/^insurance\s*#?\s*:?\s*/i, '').trim();
        combinedComments = `Partnership: ${cleanInsuranceNumber}`;
      }
      
      // Add preferred language if provided
      if (preferredLanguage && preferredLanguage.trim() !== '') {
        if (combinedComments) {
          combinedComments += '\n\nPreferred Language: ' + preferredLanguage.trim();
        } else {
          combinedComments = 'Preferred Language: ' + preferredLanguage.trim();
        }
      }
      
      // Add additional comments if provided
      if (newPatientData.comments && newPatientData.comments.trim() !== '') {
        if (combinedComments) {
          combinedComments += '\n\n' + newPatientData.comments.trim();
        } else {
          combinedComments = newPatientData.comments.trim();
        }
      }
      
      // Update newPatientData with combined comments for later use in booking
      setNewPatientData(prev => ({
        ...prev,
        comments: combinedComments
      }));
      
      if (DEBUG_MODE) {
        console.log('ðŸ” [DEBUG] Insurance Number:', insuranceNumber);
        console.log('ðŸ” [DEBUG] Preferred Language:', preferredLanguage);
        console.log('ðŸ” [DEBUG] Additional Comments:', newPatientData.comments);
        console.log('ðŸ” [DEBUG] Combined Comments:', combinedComments);
      }
      
      // Save new patient - match API spec exactly (SCHEMA_RESTRUCTURE_AND_API_REFERENCE.md)
      const patientPayload = {
        first_name: newPatientData.first_name || '',
        last_name: newPatientData.last_name || '',
        dob: newPatientData.dob || '',
        sex: parseInt(newPatientData.sex) || 2, // CRITICAL: Must be integer (1=female, 2=male, 3=unknown)
        email: newPatientData.email || '',
        mobile_phone: (newPatientData.mobile_phone || '').replace(/[^\d]/g, ''), // Remove formatting, keep only digits
        home_phone: '', // Optional field as empty string
        address: newPatientData.address || '', // NOTE: Returns null in response due to upstream limitation
        zipcode: newPatientData.zipcode || '',
        zip_id: newPatientData.zip_id, // CRITICAL: Required for upstream processing
        practice_id_encrypted: newPatientData.practice_id_encrypted,
        appointment_request_id: newPatientData.appointment_request_id || '0',
        patient_id_encrypted: '' // Optional field as empty string
      };
      
      if (DEBUG_MODE) {
        console.log('ðŸ” [DEBUG] Raw form data:', newPatientData);
        console.log('ðŸ” [DEBUG] Processed patient payload:', patientPayload);
        console.log('ðŸ” [DEBUG] Sex field - Raw:', newPatientData.sex, 'Type:', typeof newPatientData.sex);
        console.log('ðŸ” [DEBUG] Sex field - Processed:', patientPayload.sex, 'Type:', typeof patientPayload.sex);
        console.log('ðŸ” [DEBUG] Using NEW endpoint: /patient/save-new-patient (moved from /booking)');
      }
      
      const patientResponse = await saveNewPatient(patientPayload);
      
      if (patientResponse && patientResponse.APINewPatientID) {
        setSavedPatientId(patientResponse.APINewPatientID);
        setBookingData(prev => ({
          ...prev,
          patient_id: patientResponse.APINewPatientID,
          is_new_api_patient: true
        }));
        addToDebugLog('New Patient Saved', newPatientData, patientResponse);
        
        // TASK 3: Calculate nearest locations based on patient zipcode
        if (newPatientData.zipcode) {
          const nearest = await getNearestLocationsForPatient(newPatientData.zipcode);
          if (DEBUG_MODE) {
            console.log('ðŸŒ [NEW PATIENT] Nearest locations calculated:', nearest);
            console.log('ðŸŒ [NEW PATIENT] Setting nearestLocations state with', nearest.length, 'locations');
          }
          setNearestLocations(nearest);
          
          // Store patient location info for display
          if (zipcodeData) {
            setPatientLocationInfo({
              city: zipcodeData.City,
              zipcode: newPatientData.zipcode,
              state: zipcodeData.Code || 'CA'
            });
          }
          
          addToDebugLog('Nearest Locations Set (New Patient)', { 
            zipcode: newPatientData.zipcode,
            locationsCount: nearest.length,
            locations: nearest.map(loc => ({ id: loc.id, name: loc.name, distance: loc.distance }))
          });
        }
        
        // Show success message briefly
        setShowSuccessMessage(true);
        
        // Navigate to provider selection after a brief delay
        setTimeout(() => {
          setShowSuccessMessage(false);
          setCurrentStep(3);
        }, 1500);
      } else {
        throw new Error('Failed to save patient - no patient ID returned');
      }
    } catch (error) {
      if (DEBUG_MODE) {
        console.error('Error saving new patient:', error);
      }
      setApiError(`Failed to save patient: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // New patient form submission
  const handleNewPatientSubmit = async (e) => {
    e.preventDefault();
    
    // DEPRECATED: Referral Access Key validation now happens at the gate
    // The encryption key is already validated and passed as a prop
    // No need to validate again here
    
    // TASK 6: Validate Date of Birth
    const dobValidation = validateDateOfBirth(newPatientData.dob);
    if (!dobValidation.valid) {
      setApiError(dobValidation.error);
      return;
    }
    
    // TASK 6: Validate Phone Number
    const phoneValidation = validatePhoneNumber(newPatientData.mobile_phone);
    if (!phoneValidation.valid) {
      setApiError(phoneValidation.error);
      return;
    }
    
    // Validate visit type selection
    if (!selectedVisitType) {
      setApiError('Please select a visit type');
      return;
    }
    
    setLoading(true);
    setApiError('');
    
    try {
      // TASK 2: Check if "new patient" is actually an existing patient
      // Search using first name, last name, DOB (don't check address since it's dynamic)
      const existingCheckData = {
        first_name: newPatientData.first_name,
        last_name: newPatientData.last_name,
        dob: newPatientData.dob,
        practice_id: String(DEFAULT_PRACTICE_ID)
      };
      
      const existingCheck = await searchExistingPatient(existingCheckData);
      
      // Check if patient exists (PatientId > 0 and has valid data)
      const patientExists = (
        existingCheck && 
        existingCheck.PatientId > 0 && 
        existingCheck.FirstName && 
        existingCheck.LastName &&
        existingCheck.DOBString
      );
      
      if (patientExists) {
        // Additional check: compare zipcode if available
        let zipcodeMatches = false;
        if (existingCheck.Address && newPatientData.zipcode) {
          const existingZipcode = parseZipcodeFromAddress(existingCheck.Address);
          zipcodeMatches = existingZipcode === newPatientData.zipcode;
        }
        
        // TASK 2: Show custom popup instead of window.confirm
        setExistingPatientCheckResult({
          ...existingCheck,
          zipcodeMatches,
          enteredZipcode: newPatientData.zipcode
        });
        setShowExistingPatientPopup(true);
        setLoading(false);
        return; // Wait for user decision from popup
      }
      
      // If no existing patient found, proceed with saving
      await saveNewPatientAndProceed();
    } catch (error) {
      if (DEBUG_MODE) {
        console.error('Error in new patient submission:', error);
      }
      setApiError(`Failed to process patient: ${error.message}`);
      setLoading(false);
    }
  };

  // Existing patient search (modified to not auto-proceed)
  const handleExistingPatientSearch = async (e) => {
    e.preventDefault();
    
    // DEPRECATED: Referral Access Key validation now happens at the gate
    // The encryption key is already validated and passed as a prop
    // No need to validate again here
    
    // TASK 6: Validate Date of Birth
    const dobValidation = validateDateOfBirth(existingPatientData.dob);
    if (!dobValidation.valid) {
      setApiError(dobValidation.error);
      return;
    }
    
    // Validate visit type selection
    if (!selectedVisitType) {
      setApiError('Please select a visit type');
      return;
    }
    
    setLoading(true);
    setApiError('');
    
    try {
      const searchResult = await searchExistingPatient(existingPatientData);
      
      // TASK 1: Check if patient does NOT exist (PatientId=0, PracticeId=0, names null, DOBString empty, Address null)
      const patientNotFound = (
        searchResult && 
        searchResult.PatientId === 0 && 
        searchResult.PracticeId === 0 && 
        !searchResult.FirstName && 
        !searchResult.LastName && 
        (!searchResult.DOBString || searchResult.DOBString === '') &&
        !searchResult.Address
      );
      
      if (patientNotFound) {
        // TASK 1: Show popup to redirect to new patient form instead of just error
        setPatientNotFoundData(existingPatientData);
        setShowPatientNotFoundPopup(true);
        addToDebugLog('Existing Patient Not Found (PatientId=0)', existingPatientData, searchResult);
        setLoading(false);
        return;
      }
      
      // Check for successful patient search based on API reference
      if (searchResult && searchResult.PatientId && searchResult.PatientId > 0 && !searchResult.IsMultiplePatients) {
        setExistingPatientResult(searchResult);
        
        // CRITICAL: Set the patient ID for booking data
        setSavedPatientId(searchResult.PatientId);
        setBookingData(prev => ({
          ...prev,
          patient_id: searchResult.PatientId,
          is_new_api_patient: false // This is an existing patient
        }));
        
        // NEW: Capture assigned provider and location for existing patients
        // NOTE: The current API doesn't return assigned provider/location information.
        // If this data becomes available in future API updates, it should be in format:
        // searchResult.AssignedProviderId and searchResult.AssignedLocationId
        // For now, we check if these fields exist and store them
        if (searchResult.AssignedProviderId && searchResult.AssignedLocationId) {
          setAssignedProvider({
            id: searchResult.AssignedProviderId,
            name: searchResult.AssignedProviderName || 'Assigned Provider'
          });
          setAssignedLocation({
            id: searchResult.AssignedLocationId,
            name: searchResult.AssignedLocationName || 'Assigned Location'
          });
          addToDebugLog('Assigned Provider/Location Captured', {
            providerId: searchResult.AssignedProviderId,
            locationId: searchResult.AssignedLocationId
          });
        } else {
          // If no assigned provider/location, clear any previous values
          setAssignedProvider(null);
          setAssignedLocation(null);
          if (DEBUG_MODE) {
            console.log('âš ï¸ [EXISTING PATIENT] No assigned provider/location data available in API response');
            console.log('âš ï¸ [EXISTING PATIENT] Patient will be able to select any provider/location');
          }
        }
        
        // Parse zipcode from address if available
        if (searchResult.Address) {
          const zipcode = parseZipcodeFromAddress(searchResult.Address);
          if (zipcode) {
            // Lookup zipcode data
            try {
              const zipData = await lookupZipcode(zipcode);
              if (zipData && zipData.length > 0) {
                setZipcodeData(zipData[0]);
                addToDebugLog('Address Zipcode Lookup', { address: searchResult.Address, zipcode }, zipData[0]);
              }
              
              // TASK 3: Calculate nearest locations based on patient zipcode
              const nearest = await getNearestLocationsForPatient(zipcode);
              if (DEBUG_MODE) {
                console.log('ðŸŒ [EXISTING PATIENT] Nearest locations calculated:', nearest);
                console.log('ðŸŒ [EXISTING PATIENT] Setting nearestLocations state with', nearest.length, 'locations');
              }
              setNearestLocations(nearest);
              
              // Store patient location info for display
              if (zipData && zipData.length > 0) {
                setPatientLocationInfo({
                  city: zipData[0].City,
                  zipcode: zipcode,
                  state: zipData[0].Code || 'CA'
                });
              }
              
              addToDebugLog('Nearest Locations Set (Existing Patient)', { 
                zipcode,
                locationsCount: nearest.length,
                locations: nearest.map(loc => ({ id: loc.id, name: loc.name, distance: loc.distance }))
              });
            } catch (zipError) {
              if (DEBUG_MODE) {
                console.warn('Failed to lookup zipcode:', zipError);
              }
            }
          }
        }
        
        addToDebugLog('Existing Patient Found - Ready for Provider Selection', existingPatientData, {
          searchResult,
          patientIdSet: searchResult.PatientId,
          bookingDataUpdated: true
        });
        
        // Show popup instead of inline display
        setShowExistingPatientFoundPopup(true);
        
      } else if (searchResult && searchResult.IsMultiplePatients) {
        setApiError('Multiple patients found with the same information. Please contact the office directly to schedule your appointment.');
        addToDebugLog('Multiple Patients Found', existingPatientData, searchResult);
      } else if (searchResult && searchResult.raw) {
        // Handle error responses from API
        setApiError(`Patient search failed: ${searchResult.raw}`);
        addToDebugLog('Patient Search Error', existingPatientData, searchResult);
      } else {
        setApiError('Patient not found. Please check your information and try again, or register as a new patient.');
        addToDebugLog('Existing Patient Not Found', existingPatientData, searchResult);
      }
    } catch (error) {
      if (DEBUG_MODE) {
        console.error('Error searching for patient:', error);
      }
      setApiError(`Search failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load providers for step 3 (filtered for medical only)
  const loadProvidersForStep3 = async (facilityId, visitDate) => {
    try {
      // Convert date from YYYY-MM-DD to MM/DD/YYYY format for API
      let formattedDate = visitDate;
      if (visitDate && visitDate.includes('-')) {
        const dateParts = visitDate.split('-');
        formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
      }
      
      const cacheKey = `${facilityId}:${formattedDate}`;
      let allProviders;
      if (providerCache.current.has(cacheKey)) {
        allProviders = providerCache.current.get(cacheKey);
        if (DEBUG_MODE) console.log(`[CACHE HIT] Providers for ${cacheKey}`);
      } else {
        const filterData = {
          practice_id: DEFAULT_PRACTICE_ID,
          facility_id: parseInt(facilityId),
          visit_date: formattedDate,
          gender_ids: '',
          language_ids: '',
          service_offered_ids: '',
          credential_ids: '',
          patient_id: savedPatientId || (existingPatientResult ? existingPatientResult.PatientId : 0)
        };
        allProviders = await getProviders(filterData);
        providerCache.current.set(cacheKey, allProviders);
      }
      
      // Filter for medical and psychiatric providers (PA, PA-C, PMHNP, NP)
      const filteredProviders = allProviders.filter(provider => {
        const programType = getProgramType(provider.ProviderName);
        const isValidType = programType === 'Medical' || programType === 'Psychiatric';
        
        // Special filter for homepsych location
        if (parseInt(facilityId) === HOMEPSYCH_FACILITY_ID) {
          return isValidType && isAllowedHomepsychProvider(provider.ProviderName);
        }
        
        return isValidType;
      });
      
      setProviders(filteredProviders);
      addToDebugLog('Providers Loaded for Step 3', { facilityId, formattedDate, cacheKey }, {
        totalProviders: allProviders.length,
        filteredProviders: filteredProviders.length,
        filterType: 'Medical and Psychiatric (PA/PA-C/PMHNP/NP)'
      });
    } catch (error) {
      console.error('Error loading providers:', error);
      setApiError(`Failed to load providers: ${error.message}`);
    }
  };

  // Final appointment booking submission
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    setApiError('');
    
    try {
      // Prepare final booking data - convert empty strings to null for optional fields
      const patientComments = userType === 'new' ? newPatientData.comments : '';
      const finalBookingData = {
        ...bookingData,
        comments: patientComments 
          ? `Online booking - ${userType} patient\n\nPatient Comments:\n${patientComments}`
          : `Online booking - ${userType} patient`,
        // Convert empty strings to null for optional fields that might cause validation issues
        resource_type: bookingData.resource_type || null,
        flexible_date_from: bookingData.flexible_date_from || null,
        flexible_date_to: bookingData.flexible_date_to || null,
        flexible_time_from: bookingData.flexible_time_from || null,
        flexible_time_to: bookingData.flexible_time_to || null,
        // Include referral access key for middleware validation
        referral_access_key: referralAccessKey
      };
      
      addToDebugLog('Final Booking Submission', finalBookingData);
      
      // Log the exact request being sent
      console.log('ðŸ” [BOOKING] Sending request to /booking/book with data:', JSON.stringify(finalBookingData, null, 2));
      
      // Submit the appointment booking
      const bookingResponse = await bookAppointment(finalBookingData);
      
      // Log the full response for debugging
      console.log('ðŸ” [BOOKING] Full booking response:', bookingResponse);
      console.log('ðŸ” [BOOKING] Response type:', typeof bookingResponse);
      console.log('ðŸ” [BOOKING] ResultModel type:', typeof bookingResponse?.resultModel);
      console.log('ðŸ” [BOOKING] ResultModel content:', bookingResponse?.resultModel);
      addToDebugLog('Booking Response Received', finalBookingData, bookingResponse);
      
      // Check for successful booking - handle different response structures
      const isSuccess = (bookingResponse && bookingResponse.resultModel && 
                        typeof bookingResponse.resultModel === 'object' && 
                        bookingResponse.resultModel.Status === 1) ||
                       (bookingResponse && bookingResponse.Status === 1) ||
                       (bookingResponse && bookingResponse.status === 'success');
      
      if (isSuccess) {
        addToDebugLog('Appointment Booked Successfully', finalBookingData, bookingResponse);
        sessionStorage.removeItem(SESSION_FORM_KEY);
        // Show success overlay — form reset happens when the user dismisses it
        setIsSubmitted(true);
      } else {
        // Enhanced error message with full response details
        const errorDetails = {
          hasResponse: !!bookingResponse,
          hasResultModel: !!(bookingResponse?.resultModel),
          status: bookingResponse?.resultModel?.Status,
          responseMsg: bookingResponse?.resultModel?.ResponseMsg,
          fullResponse: bookingResponse
        };
        
        if (DEBUG_MODE) {
          console.error('ðŸ” [BOOKING] Booking failed - Response details:', errorDetails);
        }
        
        let errorMessage;
        
        if (bookingResponse?.resultModel === "") {
          errorMessage = "Booking failed - Backend returned empty result. This usually indicates a validation error or missing required field. Check console logs for request details.";
        } else if (bookingResponse?.resultModel?.ResponseMsg) {
          errorMessage = bookingResponse.resultModel.ResponseMsg;
        } else {
          errorMessage = `Booking failed - Status: ${bookingResponse?.resultModel?.Status || 'unknown'}, Response: ${JSON.stringify(bookingResponse || 'no response')}`;
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (DEBUG_MODE) {
        console.error('Error booking appointment:', error);
      }
      setApiError(`Booking failed: ${error.message}`);
      addToDebugLog('Booking Error', bookingData, null, error.message);
    } finally {
      setIsSubmitting(false);
    }
  };





  // -------------------------------------------------------------------------
  // Render functions have been extracted to dedicated components:
  //   DebugPanel          -> ./appointment/DebugPanel.js
  //   Step1UserType       -> ./appointment/Step1UserType.js
  //   Step2NewPatient     -> ./appointment/Step2NewPatient.js
  //   Step2ExistingPatient-> ./appointment/Step2ExistingPatient.js
  //   Step3SchedulingMethod-> ./appointment/Step3SchedulingMethod.js
  //   Step4FindNextAvailable-> ./appointment/Step4FindNextAvailable.js
  //   Step4ManualSelection-> ./appointment/Step4ManualSelection.js
  //   Step5Review         -> ./appointment/Step5Review.js
  // -------------------------------------------------------------------------

  const renderMaintenancePopup = () => {
    if (!MAINTENANCE_MODE) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-2xl animate-fadeIn">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <svg className="w-12 h-12 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <h3 className="text-2xl font-bold">Website Under Maintenance</h3>
                <p className="text-orange-100 text-sm mt-1">We're currently performing scheduled maintenance</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5 mb-6">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-orange-900 mb-2">Scheduled Maintenance</h4>
                  <p className="text-sm text-orange-800 mb-2">
                    Our appointment booking system is temporarily unavailable while we perform scheduled maintenance and updates.
                  </p>
                  <p className="text-sm text-orange-800">
                    We apologize for any inconvenience. Please check back later or contact us directly at <strong>(530) 806-0805</strong> to schedule your appointment.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <p className="text-blue-900 font-medium mb-1">Need Immediate Assistance?</p>
                  <p className="text-blue-700 text-sm">
                    Call us at <strong>(530) 806-0805</strong> to speak with our scheduling team.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="px-8 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-md hover:shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main Form Component
  return (
    <div className="max-w-4xl mx-auto">
      {/* TASK 2: Maintenance Mode Popup */}
      {renderMaintenancePopup()}
      
      {/* Disable form interaction when in maintenance mode */}
      {MAINTENANCE_MODE && (
        <div className="fixed inset-0 bg-transparent z-[99] pointer-events-none"></div>
      )}
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className={`flex items-center ${currentStep >= 1 ? 'text-orange-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 1 ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>1</div>
            <span className="ml-2 text-sm font-medium">User Type</span>
          </div>
          <div className={`w-12 h-1 ${currentStep >= 2 ? 'bg-orange-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${currentStep >= 2 ? 'text-orange-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 2 ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>2</div>
            <span className="ml-2 text-sm font-medium">Patient Info</span>
          </div>
          <div className={`w-12 h-1 ${currentStep >= 3 ? 'bg-orange-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${currentStep >= 3 ? 'text-orange-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 3 ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>3</div>
            <span className="ml-2 text-sm font-medium">Method</span>
          </div>
          <div className={`w-12 h-1 ${currentStep >= 4 ? 'bg-orange-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${currentStep >= 4 ? 'text-orange-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 4 ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>4</div>
            <span className="ml-2 text-sm font-medium">Schedule</span>
          </div>
          <div className={`w-12 h-1 ${currentStep >= 5 ? 'bg-orange-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${currentStep >= 5 ? 'text-orange-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 5 ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>5</div>
            <span className="ml-2 text-sm font-medium">Review</span>
          </div>
        </div>
      </div>
      
      {/* Navigation Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          {currentStep > 1 && (
            <button
              onClick={handlePrevStep}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          )}
          <div className="text-sm text-gray-500">
            Step {currentStep} of 5
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {currentStep > 1 && (
            <button
              onClick={handleStartOver}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Start Over
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* TASK 1: Patient Not Found Popup */}
      {showPatientNotFoundPopup && patientNotFoundData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowPatientNotFoundPopup(false);
            setApiError('Patient not found. Please verify your information and try again.');
          }}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-2xl animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center">
                <svg className="w-12 h-12 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-2xl font-bold">Patient Record Not Found</h3>
                  <p className="text-orange-100 text-sm mt-1">We couldn't find a matching patient in our system</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5 mb-6">
                <h4 className="font-semibold text-orange-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search Information Used:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Name:</span>
                    <div className="font-semibold text-gray-900">{patientNotFoundData.first_name} {patientNotFoundData.last_name}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Date of Birth:</span>
                    <div className="font-semibold text-gray-900">{patientNotFoundData.dob}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">âš ï¸ Cannot Schedule in Existing Patient Form</h4>
                    <p className="text-sm text-red-800 mb-2">
                      Since you are not in our system, you <strong>cannot</strong> schedule an appointment using the "Existing Patient" form. The appointment <strong>will not go through</strong>.
                    </p>
                    <p className="text-sm text-red-800">
                      Please use the "New Patient" form to register and schedule your appointment.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-blue-900 font-medium mb-2">Would you like to register as a new patient?</p>
                <p className="text-blue-700 text-sm mb-3">
                  Click "Yes" below to be redirected to the New Patient form with your information pre-filled.
                </p>
                <div className="bg-blue-50 border-t border-blue-300 pt-3 mt-3">
                  <p className="text-blue-900 text-sm font-medium mb-1">Need Help?</p>
                  <p className="text-blue-800 text-xs">
                    If you run into any issues with the scheduler or booking a patient, feel free to call us at <strong>(530) 806-0805</strong> or email <strong><a href="mailto:devteam@namhs.com" className="underline hover:text-blue-600">devteam@namhs.com</a></strong>. We're here to help!
                  </p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    // Redirect to new patient form with pre-filled data
                    setApiError('');
                    setShowPatientNotFoundPopup(false);
                    setUserType('new');
                    
                    // TASK 1: Auto-fill new patient form with data from search
                    setNewPatientData({
                      first_name: patientNotFoundData.first_name || '',
                      last_name: patientNotFoundData.last_name || '',
                      dob: patientNotFoundData.dob || '',
                      sex: '',
                      email: '',
                      mobile_phone: '',
                      address: '',
                      zipcode: '',
                      zip_id: null,
                      comments: '',
                      practice_id_encrypted: PRACTICE_ID_ENCRYPTED,
                      appointment_request_id: '0'
                    });
                    
                    // Reset insurance number and preferred language
                    setInsuranceNumber('');
                    setPreferredLanguage('');
                    
                    setCurrentStep(1);
                    setTimeout(() => {
                      setCurrentStep(2); // Go to new patient form with pre-filled data
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }, 100);
                    
                    addToDebugLog('Existing Patient Not Found - Redirected to New Patient Form', { 
                      searchData: patientNotFoundData,
                      reason: 'Patient not found, user chose to register as new patient'
                    });
                  }}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Yes, Register as New Patient
                </button>
                <button
                  onClick={() => {
                    // Close popup and stay on existing patient search
                    setShowPatientNotFoundPopup(false);
                    setApiError('Patient not found. Please verify your information and try again.');
                    addToDebugLog('Patient Not Found - User Chose to Retry Search', { 
                      searchData: patientNotFoundData,
                      reason: 'User wants to retry with different information'
                    });
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  No, Try Different Information
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TASK 2: Custom Existing Patient Popup */}
      {showExistingPatientPopup && existingPatientCheckResult && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowExistingPatientPopup(false);
            setLoading(false);
            setApiError('You cannot schedule as a new patient when you already have an existing patient record. Please call us at (530) 806-0805 for assistance with scheduling your appointment.');
          }}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-2xl animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center">
                <svg className="w-12 h-12 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h3 className="text-2xl font-bold">Existing Patient Record Found!</h3>
                  <p className="text-orange-100 text-sm mt-1">We found a matching patient in our system</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Patient Information Found:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Name:</span>
                    <div className="font-semibold text-gray-900">{existingPatientCheckResult.FirstName} {existingPatientCheckResult.LastName}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Date of Birth:</span>
                    <div className="font-semibold text-gray-900">{existingPatientCheckResult.DOBString}</div>
                  </div>
                  {existingPatientCheckResult.Address && (
                    <div className="bg-white p-3 rounded-lg md:col-span-2">
                      <span className="text-gray-600">Address:</span>
                      <div className="font-semibold text-gray-900">{existingPatientCheckResult.Address}</div>
                    </div>
                  )}
                  {existingPatientCheckResult.zipcodeMatches && (
                    <div className="md:col-span-2 bg-green-100 border border-green-300 p-3 rounded-lg flex items-center">
                      <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-green-800 font-medium">Zipcode matches your entry ({existingPatientCheckResult.enteredZipcode})</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">âš ï¸ Cannot Schedule in New Patient Form</h4>
                    <p className="text-sm text-red-800 mb-2">
                      We found an existing patient record with your information. You <strong>cannot</strong> schedule an appointment using the "New Patient" form. The appointment <strong>will not go through</strong>.
                    </p>
                    <p className="text-sm text-red-800">
                      Please use the "Existing Patient" form to schedule your appointment.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                <p className="text-orange-900 font-medium mb-2">Are you an existing patient?</p>
                <p className="text-orange-700 text-sm mb-3">
                  If this is your patient record, click "Yes" below to be redirected to the Existing Patient form to schedule your appointment.
                </p>
                <div className="bg-orange-50 border-t border-orange-300 pt-3 mt-3">
                  <p className="text-orange-900 text-sm font-medium mb-1">Need Help?</p>
                  <p className="text-orange-800 text-xs">
                    If you run into any issues with the scheduler or booking a patient, feel free to call us at <strong>(530) 806-0805</strong> or email <strong><a href="mailto:devteam@namhs.com" className="underline hover:text-orange-600">devteam@namhs.com</a></strong>. We're here to help!
                  </p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    // User confirmed they are existing patient - redirect and auto-fill
                    setApiError('');
                    setShowExistingPatientPopup(false);
                    setUserType('existing');
                    
                    // TASK 2: Auto-fill existing patient form data
                    setExistingPatientData({
                      first_name: existingPatientCheckResult.FirstName || '',
                      last_name: existingPatientCheckResult.LastName || '',
                      dob: existingPatientCheckResult.DOBString || '',
                      practice_id: String(DEFAULT_PRACTICE_ID)
                    });
                    
                    setCurrentStep(1);
                    setTimeout(() => {
                      setCurrentStep(2); // Go to existing patient form with pre-filled data
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }, 100);
                    
                    addToDebugLog('New Patient Redirected to Existing Patient Form', { 
                      newPatientData, 
                      existingCheckResult: existingPatientCheckResult,
                      reason: 'User confirmed existing patient status via popup'
                    });
                  }}
                  className="flex-1 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Yes, I'm an Existing Patient
                </button>
                <button
                  onClick={() => {
                    // User needs assistance - close popup and show error message
                    setShowExistingPatientPopup(false);
                    setLoading(false);
                    setApiError('You cannot schedule as a new patient when you already have an existing patient record. Please call us at (530) 806-0805 for assistance with scheduling your appointment.');
                    addToDebugLog('Existing Patient Found - User Needs Assistance', { 
                      newPatientData, 
                      existingCheckResult: existingPatientCheckResult,
                      reason: 'Cannot proceed with new patient form when existing patient record found'
                    });
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  No, Call Office for Assistance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Existing Patient Found Popup (for existing patient form) */}
      {showExistingPatientFoundPopup && existingPatientResult && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowExistingPatientFoundPopup(false);
          }}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-2xl animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center">
                <svg className="w-12 h-12 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-2xl font-bold">Patient Record Found!</h3>
                  <p className="text-green-100 text-sm mt-1">We found your patient record in our system</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 mb-6">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Your Patient Information:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Name:</span>
                    <div className="font-semibold text-gray-900">{existingPatientResult.FirstName} {existingPatientResult.LastName}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">MRN:</span>
                    <div className="font-semibold text-gray-900">{existingPatientResult.MRNNumber}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Date of Birth:</span>
                    <div className="font-semibold text-gray-900">{existingPatientResult.DOBString}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Patient ID:</span>
                    <div className="font-semibold text-gray-900">{existingPatientResult.PatientId}</div>
                  </div>
                  {existingPatientResult.Address && (
                    <div className="bg-white p-3 rounded-lg md:col-span-2">
                      <span className="text-gray-600">Address:</span>
                      <div className="font-semibold text-gray-900">{existingPatientResult.Address}</div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">âœ… Ready to Schedule</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      Your patient record has been loaded successfully. Click "Continue" below to select a provider and schedule your appointment.
                    </p>
                    <div className="bg-blue-50 border-t border-blue-300 pt-3 mt-3">
                      <p className="text-blue-900 text-xs font-medium mb-1">Need Help?</p>
                      <p className="text-blue-800 text-xs">
                        If you run into any issues with the scheduler or booking a patient, feel free to call us at <strong>(530) 806-0805</strong> or email <strong><a href="mailto:devteam@namhs.com" className="underline hover:text-blue-600">devteam@namhs.com</a></strong>. We're here to help!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    // Close popup and proceed to provider selection
                    setShowExistingPatientFoundPopup(false);
                    setShowSuccessMessage(true);
                    setTimeout(() => {
                      setShowSuccessMessage(false);
                      setCurrentStep(3); // Move to provider selection
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    }, 1500);
                  }}
                  className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  Continue to Provider Selection â†’
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Booked Successfully Overlay */}
      {isSubmitted && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-10 max-w-lg mx-4 text-center shadow-2xl">
            <div className="mb-5">
              <svg className="w-20 h-20 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Appointment Requested!</h3>
            <p className="text-gray-600 mb-2">
              Your appointment request has been successfully submitted.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              Our team will review your request and reach out to confirm the details. If you have any questions, please call us at <strong>(530) 806-0805</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setCurrentStep(1);
                  setUserType('');
                  setSavedPatientId(null);
                  setExistingPatientResult(null);
                  setNewPatientData({
                    first_name: '',
                    last_name: '',
                    dob: '',
                    sex: '',
                    email: '',
                    mobile_phone: '',
                    address: '',
                    zipcode: '',
                    zip_id: null,
                    comments: '',
                    practice_id_encrypted: PRACTICE_ID_ENCRYPTED,
                    appointment_request_id: '0'
                  });
                  setInsuranceNumber('');
                  setPreferredLanguage('');
                }}
                className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-md"
              >
                Book Another Appointment
              </button>
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  onClose();
                }}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Overlay */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 text-orange-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            {userType === 'new' ? (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Patient Saved Successfully!</h3>
                <p className="text-gray-600 mb-4">
                  Welcome, {newPatientData.first_name}! Your information has been saved. Now let's find you a provider.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Patient Found Successfully!</h3>
                <p className="text-gray-600 mb-4">
                  Welcome back, {existingPatientResult?.FirstName}! We found your patient record. Now let's find you a provider.
                </p>
                {debugMode && (
                  <div className="text-xs text-green-600 mb-2">
                    Patient ID: {existingPatientResult?.PatientId}
                  </div>
                )}
              </>
            )}
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-orange-500">Loading provider search...</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Form Content */}
      <div className="bg-white shadow-lg rounded-xl p-8 border border-gray-100">
        {currentStep === 1 && (
          <Step1UserType onSelectUserType={handleUserTypeSelection} />
        )}
        {currentStep === 2 && userType === 'new' && (
          <Step2NewPatient
            newPatientData={newPatientData}
            handleNewPatientChange={handleNewPatientChange}
            handleNewPatientSubmit={handleNewPatientSubmit}
            loading={loading}
            debugMode={debugMode}
            zipcodeData={zipcodeData}
            insuranceNumber={insuranceNumber}
            setInsuranceNumber={setInsuranceNumber}
            preferredLanguage={preferredLanguage}
            setPreferredLanguage={setPreferredLanguage}
            visitTypes={visitTypes}
            selectedVisitType={selectedVisitType}
            handleVisitTypeChange={handleVisitTypeChange}
          />
        )}
        {currentStep === 2 && userType === 'existing' && (
          <Step2ExistingPatient
            existingPatientData={existingPatientData}
            handleExistingPatientChange={handleExistingPatientChange}
            handleExistingPatientSearch={handleExistingPatientSearch}
            loading={loading}
            debugMode={debugMode}
            existingPatientResult={existingPatientResult}
            zipcodeData={zipcodeData}
            visitTypes={visitTypes}
            selectedVisitType={selectedVisitType}
            handleVisitTypeChange={handleVisitTypeChange}
          />
        )}
        {currentStep === 3 && (
          <Step3SchedulingMethod
            userType={userType}
            newPatientData={newPatientData}
            existingPatientResult={existingPatientResult}
            assignedProvider={assignedProvider}
            assignedLocation={assignedLocation}
            setSchedulingMethod={setSchedulingMethod}
            addToDebugLog={addToDebugLog}
            handleNextStep={handleNextStep}
          />
        )}
        {currentStep === 4 && schedulingMethod === 'find-next-available' && (
          <Step4FindNextAvailable
            patientLocationInfo={patientLocationInfo}
            nearestLocations={nearestLocations}
            userType={userType}
            assignedLocation={assignedLocation}
            isLoadingNextAvailable={isLoadingNextAvailable}
            selectedSearchLocation={selectedSearchLocation}
            handleFindNextAvailable={handleFindNextAvailable}
            showNextAvailable={showNextAvailable}
            nextAvailableResults={nextAvailableResults}
            searchComplete={searchComplete}
            selectedNextAvailableSlot={selectedNextAvailableSlot}
            handleSelectNextAvailableSlot={handleSelectNextAvailableSlot}
            selectedTimeSlot={selectedTimeSlot}
            selectedProvider={selectedProvider}
            selectedLocation={selectedLocation}
            selectedVisitDate={selectedVisitDate}
            bookingData={bookingData}
            setShowNextAvailable={setShowNextAvailable}
            setNextAvailableResults={setNextAvailableResults}
            handleNextStep={handleNextStep}
          />
        )}
        {currentStep === 4 && schedulingMethod === 'manual-selection' && (
          <Step4ManualSelection
            userType={userType}
            newPatientData={newPatientData}
            existingPatientResult={existingPatientResult}
            assignedProvider={assignedProvider}
            assignedLocation={assignedLocation}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            setProviders={setProviders}
            setSelectedProvider={setSelectedProvider}
            setAvailableTimeSlots={setAvailableTimeSlots}
            setSelectedTimeSlot={setSelectedTimeSlot}
            loadProvidersForStep3={loadProvidersForStep3}
            selectedVisitDate={selectedVisitDate}
            setSelectedVisitDate={setSelectedVisitDate}
            providers={providers}
            selectedProvider={selectedProvider}
            bookingData={bookingData}
            setBookingData={setBookingData}
            loadTimeSlots={loadTimeSlots}
            availableTimeSlots={availableTimeSlots}
            isLoadingTimeSlots={isLoadingTimeSlots}
            selectedTimeSlot={selectedTimeSlot}
            debugMode={debugMode}
            visitTypes={visitTypes}
            selectedVisitType={selectedVisitType}
            handleNextStep={handleNextStep}
          />
        )}
        {currentStep === 5 && (
          <Step5Review
            userType={userType}
            newPatientData={newPatientData}
            existingPatientResult={existingPatientResult}
            savedPatientId={savedPatientId}
            providers={providers}
            selectedProvider={selectedProvider}
            selectedLocation={selectedLocation}
            selectedVisitDate={selectedVisitDate}
            selectedTimeSlot={selectedTimeSlot}
            selectedNextAvailableSlot={selectedNextAvailableSlot}
            bookingData={bookingData}
            visitTypes={visitTypes}
            selectedVisitType={selectedVisitType}
            insuranceNumber={insuranceNumber}
            preferredLanguage={preferredLanguage}
            debugMode={debugMode}
            lastApiResponse={lastApiResponse}
            apiError={apiError}
            handleFinalSubmit={handleFinalSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </div>

      {/* Debug Panel */}
      <DebugPanel
        debugMode={debugMode}
        setDebugMode={setDebugMode}
        currentStep={currentStep}
        userType={userType}
        savedPatientId={savedPatientId}
        existingPatientResult={existingPatientResult}
        nearestLocations={nearestLocations}
        testServerConnection={testServerConnection}
        lastApiResponse={lastApiResponse}
        debugLog={debugLog}
      />
      
      {/* Debug Toggle - Only visible if DEBUG_MODE is enabled */}
      {DEBUG_MODE && !debugMode && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setDebugMode(true)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Show Debug Panel
          </button>
        </div>
      )}
    </div>
  );
}

export default AppointmentForm; 
